import type { GlobalMXContextValue } from '~/providers/mx-global-context/global-mx.context';
/* ********************* STORES ********************** */
import { BaseStore } from './base.store';
import { FlowStore } from './flow.store';
import { UserStore } from './user.store';
import { CompanyStore } from './company.store';
import { LegacyWorkspaceStore } from './legacy-workspace.store';
import { AutomationStore } from './automation.store';
import { ProjectStore } from './project.store';
import { FlowGalleryUiStore } from '~/components/FlowGallery/stores/flow-gallery.ui.store';
import { TemplatesUiStore } from '~/components/Templates/stores/templates.ui.store';
import { CreateFlowUiStore } from '~/components/CreateFlow/stores/create-flow.ui.store';
import { MembershipUiStore } from '~/components/Membership/stores/membership.ui.store';
import { BuilderUiStore } from './builder.ui.store';
import { AutomationUiStore } from './automation.ui.store';
import { ResponsesUiStore } from './responses.ui.store';
import { ClipboardStore } from './clipboard.store';
import { QuickPreviewUiStore } from '~/stores/quick-preview.ui.store';
import { HomeStore } from '~/stores/home.store';
import { TasksStore } from '~/components/Tasks/stores/tasks.store';
import { PaymentsUiStore } from '~/components/Payments/stores/payments.ui.store';
import { YearInReviewStore } from '~/components/YearInReview/2024/stores/yearInReview.store';

/* ********************* SERVICES ********************** */
import { FlowService } from '~/services/flow.service';
import { ImageService } from '~/services/image.service';
import { ActionsManager } from '~/services/actions-manager/actions.manager';
import { SessionService } from '~/services/session.service';
import { AutomationService } from '~/services/automation.service';
import { YearInReviewService } from '~/components/YearInReview/2024/services/yearInReview.service';
import { ProjectService } from '~/services/project.service';
import { TemplatesService } from '~/services/templates.service';
import { ContractService } from '~/services/contract.service';
import { CompanyService } from '~/services/company.service';
import { WorkspaceService } from '~/services/workspace.service';
import { MembershipService } from '~/components/Membership/services/membership.service';
import { S3UploadService } from '~/services/s3-upload.service';
import { ScheduleService } from '~/services/schedule.service';
import { FlowGalleryService } from '~/services/flow-gallery.service';
import { UserService } from '~/services/user.service';
import { TasksService } from '~/services/tasks.service';
import { PaymentsService } from '~/components/Payments/services/payments.service';
import { ActionLoggerService } from '~/services/fullstory/action-logger.service';
import { OnboardingService } from '~/services/onboarding.service';
import { ReportsService } from '~/components/Reports/services/reports.service';
import { VideoConferenceService } from '~/services/video-conference.service';
import { NylasService } from '~/services/NylasService';
import { TheKnotService } from '~/apps/main/TheKnot/the-knot.service';

/* ********************* CONTROLLERS ********************** */
import { SessionCtrl } from '~/controllers/session.controller';
import { FlowController } from '~/controllers/flow.controller';
import { ImageController } from '~/controllers/imageController/image.controller';
import { CompanyController } from '~/controllers/company.controller';
import { WorkspaceController } from '~/controllers/workspace.controller';
import { BlockController } from '~/controllers/block.controller';
import { PageController } from '~/controllers/page.controller';
import { AutomationController } from '~/controllers/automation.controller';
import { ProjectController } from '~/controllers/project.controller';
import { FlowGalleryController } from '~/components/FlowGallery/controllers/flow-gallery.controller';
import { CreateFlowController } from '~/components/CreateFlow/controllers/create-flow.controller';
import { TemplatesController } from '~/components/Templates/controllers/templates.controller';
import { VariableController } from '~/controllers/variable.controller';
import { MembershipController } from '~/components/Membership/controllers/membership.controller';
import { UserController } from '~/controllers/user.controller';
import { SessionStore } from './session.store';
import { OtamStore } from '~/components/Builder/BuilderNavBar/OtamModal/stores/Otam.store';
import { GalleryDashboardUiStore } from '~/components/GalleryDashboard/gallery-dashboard.ui.store';
import { GalleryDashboardController } from '~/components/GalleryDashboard/gallery-dashboard.controller';
import { HomeController } from '~/controllers/home.controller';
import { ScheduleController } from '~/controllers/schedule.controller';
import { AppleService } from '~/services/apple.service';
import { AppsFlyerService } from '~/services/appsflyer.service';
import { TasksController } from '~/components/Tasks/controllers/tasks.controller';
import { PaymentsController } from '~/components/Payments/controllers/payments.controller';
import { YearInReviewController } from '~/components/YearInReview/2024/controllers/yearInReview.controller';
import { AccountService } from '~/services/account.service';
import { AccountController } from '~/controllers/account.controller';
import { IApiService } from '~/services/interfaces/IApiService';
import { ReportsController } from '~/components/Reports/controllers/reports.controller';
import { UiPersistenceStore } from '~/stores/ui-persistence.store';
import { TourStore } from '~/stores/tour.store';
import { ClientPendingTaskService } from '~/services/client-pending-task.service';
import { ClientPendingTaskController } from '~/controllers/client-pending-task.controller';
import { SchedulerEngineAdapter } from '~/services/adapters/scheduler-engine.adapter';
import { PicTimeService } from '~/apps/main/PicTime/pic-time.service';
import { InjectorService } from '~/services/injector.service';
import { ServiceTemplatesService } from '~/services/service-templates.service';
import { getGlobalMXContext } from '~/providers/mx-global-context/global-mx-context.factory';
import { ClientPortalLoginService } from '~/apps/main/ClientPortal/services/client-portal-login.service';
import { ReferralsService } from '~/components/Referral/components/ReferralsPage/services/referrals.service';
import { ILocalStorage } from '~/services/interfaces/ILocalStorage';
import { PicTimeGalleriesStore } from '~/apps/main/Workspace/SidebarTools/PicTime/stores/PicTimeProjectTool.store';
import { UploadPdfStore } from '~/stores/upload-pdf.store';
import { AiPreferencesService } from '~/services/ai-preferences.service';

export class RootStore extends BaseStore {
    // We must hold instance of the global context so we can run test in parallel, the base store would be split overtime and won't be needed
    globalContext: GlobalMXContextValue;

    flowStore: FlowStore = null;
    builderUiStore: BuilderUiStore = null;
    responsesUiStore: ResponsesUiStore = null;
    automationUiStore: AutomationUiStore = null;
    workspaceStore: LegacyWorkspaceStore = null;
    automationStore: AutomationStore = null;
    yearInReviewStore: YearInReviewStore = null;
    projectStore: ProjectStore = null;
    flowGalleryUiStore: FlowGalleryUiStore = null;
    templatesUiStore: TemplatesUiStore = null;
    createFlowUiStore: CreateFlowUiStore = null;
    tasksStore: TasksStore = null;
    clipboardStore: ClipboardStore = null; // Only used in builder
    uploadPdfStore: UploadPdfStore = null;
    /**
     * @deprecated Please use the one which is provided from the globalContext instead, this will be removed as part of the root store separation
     */
    get injectorService(): InjectorService {
        return this.globalContext.injectorService;
    }

    get sessionStore(): SessionStore {
        return this.globalContext.sessionStore;
    }

    get companyStore(): CompanyStore {
        return this.globalContext.companyStore;
    }

    get userStore(): UserStore {
        return this.globalContext.userStore;
    }

    /**
     * @deprecated Please use the one which is provided from the globalContext instead, this will be removed as part of the root store separation
     */
    get membershipUiStore(): MembershipUiStore {
        return this.globalContext.membershipUiStore;
    }

    /**
     * @deprecated Please use the one which is provided from the globalContext instead, this will be removed as part of the root store separation
     */
    get storageService(): ILocalStorage {
        return this.globalContext.storageService;
    }

    appleService: AppleService = null; // Only used in one feature
    appsFlyerService: AppsFlyerService = null; // Only used in one feature AppleService
    otamStore: OtamStore = null; // Could be a hook
    quickPreviewUiStore: QuickPreviewUiStore = null;
    galleryDashboardUiStore: GalleryDashboardUiStore = null;
    paymentsUiStore: PaymentsUiStore = null;
    picTimeGalleriesStore: PicTimeGalleriesStore = null;
    homeStore: HomeStore = null;
    flowService: FlowService = null;
    imageService: ImageService = null;
    companyService: CompanyService = null;
    contractService: ContractService = null;
    workspaceService: WorkspaceService = null;
    projectService: ProjectService = null;
    actionsManager: ActionsManager = null;
    automationService: AutomationService = null;
    yearInReviewService: YearInReviewService = null;
    templatesService: TemplatesService = null;
    membershipService: MembershipService = null;
    tasksService: TasksService = null;
    s3UploadService: S3UploadService = null; // TODO: Move to global
    schedulerEngineAdapter: SchedulerEngineAdapter = null; // TODO Move to global
    flowGalleryService: FlowGalleryService = null;
    paymentsService: PaymentsService = null;
    actionLoggerService: ActionLoggerService = null;
    onboardingService: OnboardingService = null;
    accountService: AccountService = null;
    picTimeService: PicTimeService = null;
    theKnotService: TheKnotService = null;
    serviceTemplatesService: ServiceTemplatesService = null;
    nylasService: NylasService = null;
    aiPreferencesService: AiPreferencesService = null;

    /**
     * @deprecated Please use the one which is provided from the globalContext instead, this will be removed as part of the root store separation
     */
    get uiPersistenceStore(): UiPersistenceStore {
        return this.globalContext.uiPersistenceStore;
    }

    /**
     * @deprecated Please use the one which is provided from the globalContext instead, this will be removed as part of the root store separation
     */
    get clientPendingTaskService(): ClientPendingTaskService {
        return this.globalContext.clientPendingTaskService;
    }

    /**
     * @deprecated Please use the one which is provided from the globalContext instead, this will be removed as part of the root store separation
     */
    get userService(): UserService {
        return this.globalContext.userService;
    }

    get apiService(): IApiService {
        return this.globalContext.apiService;
    }

    get sessionService(): SessionService {
        return this.globalContext.sessionService;
    }

    scheduleService: ScheduleService = null;
    reportsService: ReportsService = null;
    videoConferenceService: VideoConferenceService = null;
    clientPortalLoginService: ClientPortalLoginService = null;
    referralsService: ReferralsService = null;

    flowCtrl: FlowController = null;
    blockCtrl: BlockController = null;
    variableCtrl: VariableController = null;
    companyCtrl: CompanyController = null;
    workspaceCtrl: WorkspaceController = null;
    projectCtrl: ProjectController = null;
    imageCtrl: ImageController = null;
    pageCtrl: PageController = null;
    automationCtrl: AutomationController = null;
    yearInReviewCtrl: YearInReviewController = null;
    flowGalleryCtrl: FlowGalleryController = null;
    createFlowCtrl: CreateFlowController = null;
    templatesCtrl: TemplatesController = null;
    membershipCtrl: MembershipController = null;
    tasksCtrl: TasksController = null;
    userCtrl: UserController = null;
    galleryDashboardCtrl: GalleryDashboardController = null;
    homeCtrl: HomeController = null;
    scheduleCtrl: ScheduleController = null;
    paymentsCtrl: PaymentsController = null;
    accountCtrl: AccountController = null;
    reportsCtrl: ReportsController = null;

    /**
     * @deprecated Please use the one which is provided from the globalContext instead, this will be removed as part of the root store separation
     */
    get tourStore(): TourStore {
        return this.globalContext.tourStore;
    }

    /**
     * @deprecated Please use the one which is provided from the globalContext instead, this will be removed as part of the root store separation
     */
    get clientPendingTaskCtrl(): ClientPendingTaskController {
        return this.globalContext.clientPendingTaskCtrl;
    }

    get sessionCtrl(): SessionCtrl {
        return this.globalContext.sessionCtrl;
    }

    get webSocketsAdapter() {
        return this.globalContext.webSocketsAdapter;
    }

    get webSocketsService() {
        return this.globalContext.webSocketsService;
    }

    constructor(globalContext: GlobalMXContextValue = null) {
        const globalContextValue = globalContext || getGlobalMXContext();
        super(globalContextValue);
        this.globalContext = globalContextValue;

        /* ********************* STORES ********************** */
        this.workspaceStore = new LegacyWorkspaceStore(this);
        this.projectStore = new ProjectStore();
        this.flowStore = new FlowStore(this);
        this.builderUiStore = new BuilderUiStore(this);
        this.responsesUiStore = new ResponsesUiStore(this);
        this.automationUiStore = new AutomationUiStore(this);
        this.tasksStore = new TasksStore(this);
        this.automationStore = new AutomationStore(this);
        this.yearInReviewStore = new YearInReviewStore(this);
        this.flowGalleryUiStore = new FlowGalleryUiStore(this);
        this.templatesUiStore = new TemplatesUiStore(this);
        this.createFlowUiStore = new CreateFlowUiStore(this);
        this.clipboardStore = new ClipboardStore(this);
        this.otamStore = new OtamStore(this);
        this.quickPreviewUiStore = new QuickPreviewUiStore(this);
        this.galleryDashboardUiStore = new GalleryDashboardUiStore(this);
        this.homeStore = new HomeStore(this);
        this.paymentsUiStore = new PaymentsUiStore(this);
        this.picTimeGalleriesStore = new PicTimeGalleriesStore();
        this.uploadPdfStore = new UploadPdfStore(this);

        /* ********************* SERVICES ********************** */
        this.flowGalleryService = new FlowGalleryService(this);
        this.flowService = new FlowService(this);
        this.imageService = new ImageService(this.apiService);
        this.companyService = new CompanyService(this.apiService);
        this.contractService = new ContractService(this.apiService);
        this.workspaceService = new WorkspaceService(this.apiService);
        this.projectService = new ProjectService({
            apiAdapter: this.apiService,
            clientPendingTaskCtrl: this.globalContext.clientPendingTaskCtrl
        });
        this.actionsManager = new ActionsManager(this);
        this.appleService = new AppleService(this);
        this.appsFlyerService = new AppsFlyerService(this);
        this.automationService = new AutomationService(this);
        this.yearInReviewService = new YearInReviewService(this);
        this.templatesService = new TemplatesService(
            this.apiService,
            this.flowGalleryService
        );
        this.membershipService = new MembershipService(this);
        this.tasksService = new TasksService(this.apiService);
        this.s3UploadService = new S3UploadService(this.globalContext);
        this.schedulerEngineAdapter = new SchedulerEngineAdapter(
            this.globalContext
        );
        this.scheduleService = new ScheduleService(
            this.apiService,
            this.schedulerEngineAdapter
        );
        this.paymentsService = new PaymentsService(this);
        this.accountService = new AccountService(this.apiService);
        this.picTimeService = new PicTimeService(this.apiService);
        this.theKnotService = new TheKnotService(this.apiService);

        this.actionLoggerService = new ActionLoggerService({
            logger: this.fullstoryService,
            networkStore: this.networkStore
        });
        this.onboardingService = new OnboardingService(this);
        this.reportsService = new ReportsService(this.apiService);
        this.videoConferenceService = new VideoConferenceService(
            this.apiService
        );
        this.serviceTemplatesService = new ServiceTemplatesService(
            this.apiService
        );
        this.clientPortalLoginService = new ClientPortalLoginService(
            this.apiService
        );
        this.referralsService = new ReferralsService(this.apiService);
        this.nylasService = new NylasService(this.apiService);
        this.aiPreferencesService = new AiPreferencesService(this.apiService);

        /* ********************* CONTROLLERS ********************** */
        this.flowCtrl = new FlowController(this);
        this.blockCtrl = new BlockController(this);
        this.variableCtrl = new VariableController(this);
        this.imageCtrl = new ImageController(this);
        this.companyCtrl = new CompanyController(this);
        this.workspaceCtrl = new WorkspaceController(this);
        this.projectCtrl = new ProjectController(this);
        this.pageCtrl = new PageController(this);
        this.automationCtrl = new AutomationController(this);
        this.yearInReviewCtrl = new YearInReviewController(this);
        this.flowGalleryCtrl = new FlowGalleryController(this);
        this.createFlowCtrl = new CreateFlowController(this);
        this.templatesCtrl = new TemplatesController(this);
        this.membershipCtrl = new MembershipController(this);
        this.tasksCtrl = new TasksController(this);
        this.userCtrl = new UserController(this.globalContext);
        this.galleryDashboardCtrl = new GalleryDashboardController(this);
        this.homeCtrl = new HomeController(this);
        this.scheduleCtrl = new ScheduleController(this);
        this.paymentsCtrl = new PaymentsController(this);
        this.accountCtrl = new AccountController(this);
        this.reportsCtrl = new ReportsController(this);
    }
}
